import {Link, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LoginPayload, useLogin} from '../requests/use-login';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {LinkStyle} from '@ui/buttons/external-link';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useContext, useState} from 'react';
import {
  SiteConfigContext,
  SiteConfigContextValue,
} from '../../core/settings/site-config-context';
import {useSettings} from '@ui/settings/use-settings';

interface Props {
  onTwoFactorChallenge: () => void;
}
export function LoginPage({onTwoFactorChallenge}: Props) {
  const [searchParams] = useSearchParams();
  const {pathname} = useLocation();

  const isWorkspaceLogin = pathname.includes('workspace');
  const searchParamsEmail = searchParams.get('email') || undefined;

  const {branding, registration, site, social} = useSettings();
  const siteConfig = useContext(SiteConfigContext);
  const [showPassword, setShowPassword] = useState(false);

  const demoDefaults =
    site.demo && !searchParamsEmail ? getDemoFormDefaults(siteConfig) : {};
  const form = useForm<LoginPayload>({
    defaultValues: {remember: true, email: searchParamsEmail, ...demoDefaults},
  });
  const login = useLogin(form);

  const heading = isWorkspaceLogin ? (
    <Trans
      values={{siteName: branding?.site_name}}
      message="To join your team on :siteName, login to your account"
    />
  ) : (
    <Trans message="Sign in to your account" />
    
  );

const message = !registration?.disable && (
  <div className="flex flex-col items-center gap-12 mt-16">
    <div>
      <Trans
        values={{
          a: parts => (
            <Link className={LinkStyle} to="/register">
              {parts}
            </Link>
          ),
        }}
        message="Don't have an account? <a>Sign up.</a>"
      />
    </div>
    <div>
      <span>Got a query? </span>
      <Link to="/contact" className={LinkStyle}>
        Contact us.
      </Link>
    </div>
  </div>
);




  const isInvalid = !!Object.keys(form.formState.errors).length;

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Login" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={payload => {
          login.mutate(payload, {
            onSuccess: response => {
              if (response.two_factor) {
                onTwoFactorChallenge();
              }
            },
          });
        }}
      >
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          label={<Trans message="Email" />}
          disabled={!!searchParamsEmail}
          invalid={isInvalid}
          required
        />
       <FormTextField
        className="mb-12"
        name="password"
        type={showPassword ? 'text' : 'password'}
        label={<Trans message="Password" />}
        invalid={isInvalid}
        required
        labelSuffix={
          <Link className={LinkStyle} to="/forgot-password" tabIndex={-1}>
            <Trans message="Forgot your password?" />
          </Link>
        }
        endAdornment={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="ml-8 pr-4 text-gray-600 hover:text-primary focus:outline-none"
            tabIndex={-1}
          >
            {showPassword ? (
              // Eye Slash SVG
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                      4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 3l18 18" />
            </svg>
            ) : (
              // Eye Open SVG (larger)
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 
                      4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
            </svg>
            )}
          </button>
        }
      />


        <FormCheckbox name="remember" className="mb-1 block">
          <Trans message="Stay signed in for a month" />
        </FormCheckbox>
        
        <Button
          className="mt-38 block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={login.isPending}
//           style={{
//             backgroundColor: '#6cf3fd',
//             color: '#000',
//           }}
        >
          <Trans message="Continue" />
        </Button>
      </Form>
      <SocialAuthSection
        dividerMessage={
          social?.compact_buttons ? (
            <Trans message="Or sign in with" />
          ) : (
            <Trans message="OR" />
          )
        }
        
      />

    </AuthLayout>
    
  );
}

function getDemoFormDefaults(siteConfig: SiteConfigContextValue) {
  if (siteConfig.demo.loginPageDefaults === 'randomAccount') {
    // random number between 0 and 100, padded to 3 digits
    const number = Math.floor(Math.random() * 100) + 1;
    const paddedNumber = String(number).padStart(3, '0');
    return {
      email: `admin@demo${paddedNumber}.com`,
      password: 'admin',
    };
  } else {
    return {
      email: siteConfig.demo.email ?? 'admin@admin.com',
      password: siteConfig.demo.password ?? 'admin',
    };
  }
}